import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { TextField, Stack, Typography, Button } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';

import Modal from 'components/_layouts/Modal';
import { AppContext } from 'views/App/context';
import useReviewWorkflows from 'views/App/Portal/PerformanceReporting/ReviewWorkflows/_data_sources/useReviewWorkflows';
import type { TemplateReviewWorkflow } from '../../_types';
import ReviewWorkflowPicker from '../../_components/ReviewWorkflowPicker';

interface PublishTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { changeNote: string; reviewWorkflowId?: number }) => Promise<void> | void;
  isSaving: boolean;
  existingReviewWorkflows?: TemplateReviewWorkflow[];
  showWorkflows?: boolean;
}

const PublishTemplateDialog: React.FC<PublishTemplateDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isSaving,
  existingReviewWorkflows,
  showWorkflows = true,
}) => {
  const { currentOrganizationId } = useContext(AppContext);
  const [selectedReviewWorkflowId, setSelectedReviewWorkflowId] = useState<number | ''>('');
  const [changeNote, setChangeNote] = useState('');
  const [publishTouched, setPublishTouched] = useState(false);

  const queryParams = useMemo(() => {
    return {
      organizationId: currentOrganizationId,
    };
  }, [currentOrganizationId]);
  const reviewWorkflowsQuery = useReviewWorkflows(queryParams, { enabled: showWorkflows });
  const reviewWorkflows = reviewWorkflowsQuery.data?.pages.flatMap((page) => page.content) ?? [];
  const { isLoading: isReviewWorkflowsLoading } = reviewWorkflowsQuery;

  const reviewWorkflowOptions = useMemo(() => {
    if (!showWorkflows) return [];
    const fromExisting = (existingReviewWorkflows ?? []).map((w) => ({ value: w.id, label: w.name }));
    const fromApi = reviewWorkflows.map((w) => ({ value: w.reviewWorkflowId, label: w.name }));
    const byValue = new Map<number, { value: number; label: string }>();
    [...fromExisting, ...fromApi].forEach((opt) => byValue.set(opt.value, opt));
    return Array.from(byValue.values());
  }, [showWorkflows, existingReviewWorkflows, reviewWorkflows]);

  const resetState = useCallback(() => {
    setSelectedReviewWorkflowId('');
    setChangeNote('');
    setPublishTouched(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    } else if (showWorkflows) {
      const activeId = existingReviewWorkflows?.find((w) => w.isActive)?.id;
      if (activeId != null) {
        setSelectedReviewWorkflowId(activeId);
      }
    }
  }, [open, resetState, existingReviewWorkflows, showWorkflows]);

  const reviewWorkflowError = showWorkflows && publishTouched && !selectedReviewWorkflowId;
  const changeNoteError = publishTouched && !changeNote.trim();
  const canPublish = showWorkflows
    ? Boolean(selectedReviewWorkflowId) && Boolean(changeNote.trim())
    : Boolean(changeNote.trim());

  const reviewWorkflowHelperText = useMemo(() => {
    if (!showWorkflows) return '';

    if (reviewWorkflowError) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowRequired');
    }

    if (isReviewWorkflowsLoading) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowLoading');
    }

    if (!reviewWorkflowOptions.length) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowEmpty');
    }

    return t('performanceReporting.templates.builder.publishModal.reviewWorkflowHelper');
  }, [showWorkflows, isReviewWorkflowsLoading, reviewWorkflowError, reviewWorkflowOptions.length]);

  const changeNoteHelperText = changeNoteError
    ? t('performanceReporting.templates.builder.publishModal.changeNoteRequired')
    : t('performanceReporting.templates.builder.publishModal.changeNoteHelper');

  const handleConfirm = useCallback(async () => {
    const trimmedChangeNote = changeNote.trim();
    setPublishTouched(true);

    if (showWorkflows && !selectedReviewWorkflowId) {
      return;
    }

    if (!trimmedChangeNote) {
      return;
    }

    await onConfirm({
      changeNote: trimmedChangeNote,
      ...(showWorkflows && selectedReviewWorkflowId ? { reviewWorkflowId: Number(selectedReviewWorkflowId) } : {}),
    });
  }, [changeNote, onConfirm, selectedReviewWorkflowId, showWorkflows]);

  const isPublishDisabled = showWorkflows
    ? !canPublish || isSaving || isReviewWorkflowsLoading || !reviewWorkflowOptions.length
    : !canPublish || isSaving;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('performanceReporting.templates.builder.publishModal.title')}
      actions={
        <>
          <Button
            variant='outlined'
            color='primary'
            onClick={onClose}
            disabled={isSaving}
          >
            {t('performanceReporting.templates.builder.publishModal.keepEditing')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirm}
            disabled={isPublishDisabled}
            loading={isSaving}
            loadingPosition='end'
          >
            {t('performanceReporting.templates.builder.publishModal.publish')}
          </Button>
        </>
      }
    >
      <Stack
        spacing={3}
        sx={{ mt: 1 }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
        >
          {showWorkflows
            ? t('performanceReporting.templates.builder.publishModal.description')
            : t('performanceReporting.templates.builder.publishModal.lexipolDescription')}
        </Typography>
        {showWorkflows && (
          <ReviewWorkflowPicker
            id='publish-review-workflow'
            options={reviewWorkflowOptions}
            isLoading={isReviewWorkflowsLoading}
            value={selectedReviewWorkflowId}
            onChange={(value) => setSelectedReviewWorkflowId(value ?? '')}
            label={t('performanceReporting.templates.builder.publishModal.reviewWorkflowLabel')}
            placeholder={t('performanceReporting.templates.builder.publishModal.reviewWorkflowPlaceholder')}
            required
            error={reviewWorkflowError}
            helperText={reviewWorkflowHelperText}
            disabled={isReviewWorkflowsLoading || !reviewWorkflowOptions.length}
          />
        )}
        <TextField
          label={t('performanceReporting.templates.builder.publishModal.changeNoteLabel')}
          value={changeNote}
          onChange={(event) => setChangeNote(event.target.value)}
          fullWidth
          multiline
          minRows={3}
          required
          error={changeNoteError}
          helperText={changeNoteHelperText}
        />
      </Stack>
    </Modal>
  );
};

export default PublishTemplateDialog;
